import { Injectable, inject } from '@angular/core';
import { map, Observable } from 'rxjs';
import { ApiClientService } from '../../../core/http/api-client.service';
import {
  CreateDeviceRequest,
  CreateDeviceLifecycleEventRequest,
  DeviceDetailDto,
  DeviceDetailItem,
  DeviceDto,
  DeviceLifecycleEventDto,
  DeviceLifecycleEventItem,
  DeviceListSort,
  DevicePage,
  DevicePageDto,
  DeviceItem,
  DevicePhoto,
  DeviceStatus,
  UpdateDeviceRequest,
  UpdateDeviceLifecycleEventRequest,
} from './devices.models';

@Injectable({
  providedIn: 'root'
})
export class DevicesService {
  private readonly apiClient = inject(ApiClientService);

  public getList(companyId?: string): Observable<DeviceItem[]> {
    return this.apiClient.get<DeviceDto[] | null>(
      '/devices',
      companyId ? { companyId } : undefined,
    ).pipe(
      map((items) => {
        if (!Array.isArray(items)) {
          return [];
        }

        return items.map((item) => this.map(item));
      }),
    );
  }

  public getPage(params: {
    companyId?: string;
    limit?: number;
    cursor?: string | null;
    sort?: DeviceListSort;
    filters?: {
      status?: DeviceStatus;
      clinicId?: string;
      deviceTypeId?: string;
      q?: string;
    };
  }): Observable<DevicePage> {
    return this.apiClient.get<DevicePageDto>(
      '/devices',
      {
        companyId: params.companyId,
        limit: params.limit,
        cursor: params.cursor || undefined,
        sort: params.sort,
        status: params.filters?.status,
        clinicId: params.filters?.clinicId,
        deviceTypeId: params.filters?.deviceTypeId,
        q: params.filters?.q,
      },
    ).pipe(
      map((page) => ({
        items: Array.isArray(page.items)
          ? page.items.map((item) => this.map(item))
          : [],
        nextCursor: page.nextCursor ?? null,
        hasMore: !!page.hasMore,
      })),
    );
  }

  public getDetailById(id: string, companyId?: string): Observable<DeviceDetailItem> {
    return this.apiClient.get<DeviceDetailDto>(
      `/devices/${encodeURIComponent(id)}/detail`,
      companyId ? { companyId } : undefined,
    ).pipe(
      map((item) => ({
        ...this.map(item),
        lifecycleEvents: Array.isArray(item.lifecycleEvents)
          ? item.lifecycleEvents.map((event) => this.mapLifecycleEvent(event))
          : [],
      })),
    );
  }

  public create(payload: CreateDeviceRequest, companyId?: string): Observable<DeviceItem> {
    return this.apiClient.post<DeviceDto, CreateDeviceRequest>(
      '/devices',
      payload,
      companyId ? { companyId } : undefined,
    ).pipe(
      map((item) => this.map(item)),
    );
  }

  public update(id: string, payload: UpdateDeviceRequest, companyId?: string): Observable<DeviceItem> {
    return this.apiClient.patch<DeviceDto, UpdateDeviceRequest>(
      `/devices/${id}`,
      payload,
      companyId ? { companyId } : undefined,
    ).pipe(
      map((item) => this.map(item)),
    );
  }

  public archive(id: string, companyId?: string): Observable<DeviceItem> {
    return this.apiClient.patch<DeviceDto, Record<string, never>>(
      `/devices/${id}/archive`,
      {},
      companyId ? { companyId } : undefined,
    ).pipe(
      map((item) => this.map(item)),
    );
  }

  public delete(id: string, companyId?: string): Observable<{ ok: boolean }> {
    return this.apiClient.delete<{ ok: boolean }>(
      `/devices/${id}`,
      companyId ? { companyId } : undefined,
    );
  }

  public uploadPhotos(id: string, files: File[], companyId?: string): Observable<DeviceItem> {
    const body = new FormData();
    for (const file of files) {
      body.append('files', file);
    }

    return this.apiClient.post<DeviceDto, FormData>(
      `/devices/${id}/files`,
      body,
      companyId ? { companyId } : undefined,
    ).pipe(
      map((item) => this.map(item)),
    );
  }

  public removePhoto(id: string, key: string, companyId?: string): Observable<DeviceItem> {
    return this.apiClient.patch<DeviceDto, { key: string }>(
      `/devices/${id}/files/remove`,
      { key },
      companyId ? { companyId } : undefined,
    ).pipe(
      map((item) => this.map(item)),
    );
  }

  public getDevicePhotoDownloadUrl(
    id: string,
    key: string,
    companyId?: string,
  ): Observable<{ key: string; fileName: string; url: string }> {
    return this.apiClient.get<{ key: string; fileName: string; url: string }>(
      `/devices/${id}/files/download`,
      {
        key,
        companyId: companyId ?? undefined,
      },
    );
  }

  public getLifecycleEvents(id: string, companyId?: string): Observable<DeviceLifecycleEventItem[]> {
    return this.apiClient.get<DeviceLifecycleEventDto[] | null>(
      `/devices/${id}/lifecycle-events`,
      companyId ? { companyId } : undefined,
    ).pipe(
      map((items) => {
        if (!Array.isArray(items)) {
          return [];
        }

        return items.map((item) => this.mapLifecycleEvent(item));
      }),
    );
  }

  public createLifecycleEvent(
    id: string,
    payload: CreateDeviceLifecycleEventRequest,
    companyId?: string,
  ): Observable<DeviceLifecycleEventItem> {
    return this.apiClient.post<DeviceLifecycleEventDto, CreateDeviceLifecycleEventRequest>(
      `/devices/${id}/lifecycle-events`,
      payload,
      companyId ? { companyId } : undefined,
    ).pipe(
      map((item) => this.mapLifecycleEvent(item)),
    );
  }

  public uploadLifecycleEventFiles(
    deviceId: string,
    eventId: string,
    files: File[],
    companyId?: string,
  ): Observable<DeviceLifecycleEventItem> {
    const body = new FormData();
    for (const file of files) {
      body.append('files', file);
    }

    return this.apiClient.post<DeviceLifecycleEventDto, FormData>(
      `/devices/${deviceId}/lifecycle-events/${eventId}/files`,
      body,
      companyId ? { companyId } : undefined,
    ).pipe(
      map((item) => this.mapLifecycleEvent(item)),
    );
  }

  public getLifecycleAttachmentDownloadUrl(
    deviceId: string,
    eventId: string,
    key: string,
    companyId?: string,
  ): Observable<{ key: string; fileName: string; url: string }> {
    return this.apiClient.get<{ key: string; fileName: string; url: string }>(
      `/devices/${deviceId}/lifecycle-events/${eventId}/files/download`,
      {
        key,
        companyId: companyId ?? undefined,
      },
    );
  }

  public updateLifecycleEvent(
    deviceId: string,
    eventId: string,
    payload: UpdateDeviceLifecycleEventRequest,
    companyId?: string,
  ): Observable<DeviceLifecycleEventItem> {
    return this.apiClient.patch<DeviceLifecycleEventDto, UpdateDeviceLifecycleEventRequest>(
      `/devices/${deviceId}/lifecycle-events/${eventId}`,
      payload,
      companyId ? { companyId } : undefined,
    ).pipe(
      map((item) => this.mapLifecycleEvent(item)),
    );
  }

  public deleteLifecycleEvent(
    deviceId: string,
    eventId: string,
    companyId?: string,
  ): Observable<{ ok: boolean }> {
    return this.apiClient.delete<{ ok: boolean }>(
      `/devices/${deviceId}/lifecycle-events/${eventId}`,
      companyId ? { companyId } : undefined,
    );
  }

  private map(item: DeviceDto): DeviceItem {
    return {
      id: item.id,
      companyDisplay: item.companyDisplay ?? null,
      clinicId: item.clinicId,
      clinicName: item.clinicName ?? null,
      deviceTypeId: item.deviceTypeId,
      deviceTypeName: item.deviceTypeName ?? null,
      serialNumber: item.serialNumber,
      brand: item.brand,
      model: item.model,
      manufactureDateText: item.manufactureDateText,
      lastServiceDateText: item.lastServiceDateText,
      addedDateText: item.addedDateText,
      photos: this.mapPhotos(item.photos),
      notes: item.notes,
      status: item.status,
      createdAt: item.createdAt,
      updatedAt: item.updatedAt,
    };
  }

  private mapPhotos(raw: unknown): DevicePhoto[] {
    if (!Array.isArray(raw)) {
      return [];
    }

    return raw
      .flatMap((item): DevicePhoto[] => {
        if (!item || typeof item !== 'object') {
          return [];
        }

        const key = 'key' in item ? String(item.key ?? '').trim() : '';
        const url = 'url' in item ? String(item.url ?? '').trim() : '';
        if (!key || !url) {
          return [];
        }

        return [{ key, url }];
      });
  }

  private mapLifecycleEvent(item: DeviceLifecycleEventDto): DeviceLifecycleEventItem {
    const attachments = Array.isArray(item.attachments)
      ? item.attachments.flatMap((entry) => {
          if (!entry || typeof entry !== 'object') {
            return [];
          }

          const key = String(entry.key ?? '').trim();
          const name = String(entry.name ?? '').trim();
          const url = String(entry.url ?? '').trim();
          if (!key || !url) {
            return [];
          }

          return [{ key, name: name || key, url }];
        })
      : (Array.isArray(item.photos)
        ? item.photos.flatMap((url) => {
            const normalizedUrl = String(url ?? '').trim();
            if (!normalizedUrl) {
              return [];
            }

            const rawName = normalizedUrl.split('?')[0].split('/').pop() || normalizedUrl;
            return [{ key: normalizedUrl, name: rawName, url: normalizedUrl }];
          })
        : []);

    return {
      id: item.id,
      companyId: item.companyId,
      deviceId: item.deviceId,
      eventType: item.eventType,
      title: item.title,
      notes: item.notes,
      photos: Array.isArray(item.photos) ? item.photos : [],
      attachments,
      performedAt: item.performedAt,
      createdByUserId: item.createdByUserId,
      createdAt: item.createdAt,
    };
  }
}
